import { type JSX, useEffect, useState } from "react";

import CampaignsEmptyPlaceholder from "common/components/CampaignsEmptyPlaceholder";
import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import WorkspaceAccountSelector from "common/components/WorkspaceAccountSelector";
import useCampaigns from "common/datahooks/useCampaigns";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { NavTabsContentContainer } from "common/layouts/WorkspaceLayout";
import type { Campaign } from "common/types";

import { CAMPAIGNS_TAB_ITEMS } from "../../constants";
import useCampaignsFilters from "../../hooks/useCampaignsFilters";
import CreateCampaignButton from "./CreateCampaignButton";
import DesktopTable from "./DesktopLayout/DesktopTable";
import ManageCampaigns from "./ManageCampaigns";
import MobileFilters from "./MobileLayout/MobileFilters";
import MobileList from "./MobileLayout/MobileList";
import StateFilter from "./StateFilter";

import NoResultsImage from "assets/images/empty-placeholders/result-not-found.png";

export default function CampaignsList() {
  const isDesktop = useTwBreakpoint("lg");
  const [selectedCampaigns, setSelectedCampaigns] = useState<Campaign[]>([]);

  const { campaigns, isLoadingCampaigns, campaignsError, refetchCampaigns } =
    useCampaigns();

  const {
    searchParams,
    setSearchParams,
    filteredCampaigns,
    hasFilters,
    searchText,
    setSearchText,
    clearFilters,
  } = useCampaignsFilters(campaigns);

  useEffect(() => {
    setSelectedCampaigns((prev) =>
      prev.filter(({ id }) =>
        filteredCampaigns.some(
          (filteredCampaign) => filteredCampaign.id === id,
        ),
      ),
    );
  }, [filteredCampaigns]);

  const manageCampaignsComponent = !!selectedCampaigns.length && (
    <RestrictedComponent visibleForPermissions={["campaigns_full"]}>
      <ManageCampaigns
        selectedCampaigns={selectedCampaigns}
        setSelectedCampaigns={setSelectedCampaigns}
      />
    </RestrictedComponent>
  );

  const isEmpty = !isLoadingCampaigns && (!campaigns || !campaigns.length);

  let mainContent: JSX.Element;
  if (campaignsError) {
    mainContent = (
      <ErrorPlaceholder
        errorMessage="Could not display Campaigns"
        onRetry={refetchCampaigns}
      />
    );
  } else if (isEmpty) {
    mainContent = <CampaignsEmptyPlaceholder />;
  } else {
    const Component = isDesktop ? DesktopTable : MobileList;

    mainContent = (
      <>
        {!isDesktop && <CreateCampaignButton />}

        <div className="flex w-full items-center gap-2 lg:gap-2.5">
          <SearchInput
            className="max-lg:flex-1 lg:max-w-80"
            placeholder="Search campaigns"
            value={searchText}
            variant={isDesktop ? "sm" : "lg"}
            onChange={(event) => setSearchText(event.target.value)}
            onClear={() => setSearchText("")}
          />

          {isDesktop ? (
            <>
              <StateFilter
                selectedFilters={searchParams.status}
                setFilters={(value) => setSearchParams({ status: value })}
                campaigns={campaigns}
              />

              <WorkspaceAccountSelector
                selection={searchParams.owners}
                setSelection={(value) => setSearchParams({ owners: value })}
                className="w-36 mr-auto"
                size="sm"
              />

              {manageCampaignsComponent}
            </>
          ) : (
            <MobileFilters
              campaigns={campaigns}
              currentFilters={searchParams}
              clearFilters={clearFilters}
              applyFilters={setSearchParams}
            />
          )}
        </div>

        {hasFilters && !isLoadingCampaigns && !filteredCampaigns.length ? (
          <EmptyPlaceholder
            title="No results found"
            subtitle="There were no results that match your criteria"
            imageSrc={NoResultsImage}
            actionComponent={
              <Button onClick={clearFilters}>Clear Search</Button>
            }
          />
        ) : (
          <Component
            isLoading={isLoadingCampaigns}
            campaigns={filteredCampaigns}
            selectedCampaigns={selectedCampaigns}
            setSelectedCampaigns={setSelectedCampaigns}
          />
        )}
      </>
    );
  }

  return (
    <NavTabsContentContainer
      primaryActionsComponent={
        <RenderIf condition={!isEmpty}>
          {isDesktop ? <CreateCampaignButton /> : manageCampaignsComponent}
        </RenderIf>
      }
      tabItems={CAMPAIGNS_TAB_ITEMS}
    >
      {mainContent}
    </NavTabsContentContainer>
  );
}
